/**
 * AISStream.io live-AIS WebSocket client. There are two ways we reach AISStream:
 *
 *   - PROXY (browser dev/preview): connect to the SAME-ORIGIN path /aisstream/...,
 *     which the Vite dev server relays to wss://stream.aisstream.io presenting
 *     AISStream's own origin and injecting the AISSTREAM_KEY from .env.local
 *     server-side, so the key never reaches the browser bundle.
 *   - DIRECT (packaged app / any build with no proxy): connect straight to
 *     wss://stream.aisstream.io/v0/stream with the API key sent INSIDE the
 *     subscribe message. The packaged APK has no Vite proxy, so it must use this
 *     path. A wss:// handshake isn't CORS-restricted the way fetch is, so a
 *     direct connection works from the WebView's https origin.
 *
 * Either way we send the documented subscribe message (API key + bounding box),
 * parse PositionReport + ShipStaticData into partial targets, and reconnect with
 * backoff. A key typed into Settings always takes precedence over the baked key.
 */
import { Capacitor } from '@capacitor/core';
import type { AisTarget, AisConnState } from '@/store/ais';

/** AISStream's real WebSocket endpoint (used directly by the packaged app). */
export const AISSTREAM_WS_URL = 'wss://stream.aisstream.io/v0/stream';

/** The API key baked into the build (like VITE_MAPTILER_KEY). Present in the
 *  packaged app so the online source works with no dev proxy and no user entry. */
function bakedAisKey(): string {
  const env = (import.meta.env as Record<string, string | undefined>).VITE_AISSTREAM_KEY ?? '';
  return env.trim();
}

/** Effective key: a Settings entry wins, otherwise the baked build key. The dev
 *  proxy can still inject a key server-side when neither is present (dev only). */
export function aisStreamKey(settingsKey: string): string {
  const s = settingsKey.trim();
  return s || bakedAisKey();
}

/** Whether the dev/preview proxy has a server-side key configured. VITE_AISSTREAM_PROXY
 *  is a non-secret flag (like VITE_ADMIRALTY_PROXY); the key itself stays out of the bundle. */
export function aisProxyConfigured(): boolean {
  const flag = (import.meta.env as Record<string, string | undefined>).VITE_AISSTREAM_PROXY ?? '';
  return flag.trim() !== '';
}

/** How the client will reach AISStream. 'nokey' means we have neither a key to
 *  send directly nor a dev proxy to inject one, so we can't connect. */
export type AisTransportMode = 'direct' | 'proxy' | 'nokey';

export interface AisTransport {
  mode: AisTransportMode;
  /** WebSocket URL to open (unset for 'nokey'). */
  url?: string;
  /** Key to send in the subscribe message. Empty in 'proxy' mode means the proxy
   *  injects the server-side key; in 'direct' mode this is always a real key. */
  subscribeKey?: string;
}

/**
 * Pure transport chooser (unit-tested). The packaged/native app has no Vite
 * proxy, so it must connect DIRECTLY and therefore needs a key. A browser build
 * prefers the proxy when it's configured (keeps the key server-side in dev), and
 * otherwise connects directly if it has a baked/Settings key. With neither, we
 * can't connect. Note: the proxy flag is a VITE_ var and so is present in the
 * native bundle too, which is exactly why 'native' is checked FIRST and never
 * defers to it.
 */
export function chooseAisTransport(opts: {
  native: boolean;
  proxyConfigured: boolean;
  /** Key typed into Settings (wins when present). */
  settingsKey: string;
  /** Key baked into the build (VITE_AISSTREAM_KEY). */
  bakedKey: string;
  wsProtocol?: string;
  wsHost?: string;
}): AisTransport {
  const settings = opts.settingsKey.trim();
  const effective = settings || opts.bakedKey.trim();
  if (opts.native) {
    // No dev proxy in the packaged app: must connect directly, so it needs a key.
    return effective
      ? { mode: 'direct', url: AISSTREAM_WS_URL, subscribeKey: effective }
      : { mode: 'nokey' };
  }
  if (opts.proxyConfigured) {
    // Browser dev/preview: relay through the same-origin proxy. Send only a
    // Settings key; an empty key lets the proxy inject the server-side one, so a
    // baked key never has to travel in the request when the proxy can supply it.
    const proto = opts.wsProtocol === 'https:' ? 'wss' : 'ws';
    const host = opts.wsHost ?? '';
    return { mode: 'proxy', url: `${proto}://${host}/aisstream/v0/stream`, subscribeKey: settings };
  }
  // Keyed browser build with no proxy (e.g. a plain static deploy): connect direct.
  if (effective) return { mode: 'direct', url: AISSTREAM_WS_URL, subscribeKey: effective };
  return { mode: 'nokey' };
}

/** Resolve the transport for the current runtime (reads Capacitor + location). */
export function resolveAisTransport(settingsKey: string): AisTransport {
  return chooseAisTransport({
    native: Capacitor.isNativePlatform(),
    proxyConfigured: aisProxyConfigured(),
    settingsKey,
    bakedKey: bakedAisKey(),
    wsProtocol: typeof location !== 'undefined' ? location.protocol : 'https:',
    wsHost: typeof location !== 'undefined' ? location.host : '',
  });
}

/** AIS ship-type code (0..99) to a short label. */
function shipTypeLabel(n?: number): string | undefined {
  if (n == null || n === 0) return undefined;
  if (n === 30) return 'Fishing';
  if (n === 31 || n === 32 || n === 52) return 'Tug / towing';
  if (n === 36) return 'Sailing';
  if (n === 37) return 'Pleasure craft';
  if (n >= 40 && n <= 49) return 'High-speed craft';
  if (n === 50) return 'Pilot';
  if (n === 51) return 'Search & rescue';
  if (n === 55) return 'Law enforcement';
  if (n >= 60 && n <= 69) return 'Passenger';
  if (n >= 70 && n <= 79) return 'Cargo';
  if (n >= 80 && n <= 89) return 'Tanker';
  return 'Other';
}

/** [west, south, east, north]. */
export type Bbox = [number, number, number, number];

/**
 * Minimum half-width/height (degrees) the subscription box is grown to. AISStream
 * only sends targets INSIDE the box, so a tight zoom (or a view sitting just off
 * the boat) could ask for such a small patch that real ships nearby never arrive
 * — "no ships" that is really "box too small". We never subscribe to less than
 * roughly this half-size around the centre so there's always a sensible catchment
 * around the boat. ~0.3° lon / 0.2° lat is about 18 nm across at UK latitudes.
 */
export const AIS_MIN_HALF_LON = 0.3;
export const AIS_MIN_HALF_LAT = 0.2;

/**
 * The bounding box to subscribe to: the current chart view when we have it (so it
 * follows what the skipper is looking at), the boat otherwise, but never smaller
 * than the minimum catchment above so a tight zoom can't starve the feed. Pure and
 * unit-tested. `view` is [west, south, east, north]; `boat`/`fallback` are [lon, lat].
 */
export function aisSubscriptionBox(
  view: Bbox | null,
  boat: [number, number] | null,
  fallback: [number, number],
): Bbox {
  if (view) {
    const cx = (view[0] + view[2]) / 2;
    const cy = (view[1] + view[3]) / 2;
    const halfLon = Math.max(AIS_MIN_HALF_LON, Math.abs(view[2] - view[0]) / 2);
    const halfLat = Math.max(AIS_MIN_HALF_LAT, Math.abs(view[3] - view[1]) / 2);
    return [cx - halfLon, cy - halfLat, cx + halfLon, cy + halfLat];
  }
  const p = boat ?? fallback;
  return [p[0] - AIS_MIN_HALF_LON, p[1] - AIS_MIN_HALF_LAT, p[0] + AIS_MIN_HALF_LON, p[1] + AIS_MIN_HALF_LAT];
}

export function buildAisSubscribeMessage(key: string, b: Bbox): string {
  // AISStream BoundingBoxes are corner pairs in [lat, lon] order. With no
  // Settings/baked key we send NO APIKey field; the proxy injects the server-side one.
  return JSON.stringify({
    ...(key ? { APIKey: key } : {}),
    BoundingBoxes: [
      [
        [b[1], b[0]],
        [b[3], b[2]],
      ],
    ],
    // Class A position (PositionReport) PLUS Class B position
    // (Standard/Extended ClassB reports) — leisure craft transmit Class B, so
    // leaving these out means an empty plot in a place full of yachts and small
    // motorboats. Static-data types give us names/type/size for both classes.
    FilterMessageTypes: [
      'PositionReport',
      'StandardClassBPositionReport',
      'ExtendedClassBPositionReport',
      'ShipStaticData',
      'StaticDataReport',
    ],
  });
}

/** AIS length/width dimensions (metres from the reference point to each side). */
export interface AisDimension {
  A?: number;
  B?: number;
  C?: number;
  D?: number;
}

/** Position fields shared by Class A and Class B position reports. */
export interface AisPositionCommon {
  UserID: number;
  Latitude: number;
  Longitude: number;
  Cog?: number;
  Sog?: number;
  TrueHeading?: number;
}

/** AISStream JSON frame shape (only the fields we read). Exported for the parser test. */
export interface AisStreamFrame {
  error?: string;
  MessageType?: string;
  /**
   * AISStream repeats a few useful values outside the raw AIS body. Crucially it
   * carries the last-known position here (lowercase latitude/longitude) as well as
   * inside Message. Some frames land with the body position missing or zeroed but
   * a good MetaData fix, so we read this as a fallback rather than drop the vessel.
   */
  MetaData?: { ShipName?: string; MMSI?: number; latitude?: number; longitude?: number; time_utc?: string };
  Message?: {
    PositionReport?: AisPositionCommon;
    /** Class B (message type 18) — the standard leisure-craft position report. */
    StandardClassBPositionReport?: AisPositionCommon;
    /** Class B (message type 19) — also carries name/type/size in the same frame. */
    ExtendedClassBPositionReport?: AisPositionCommon & {
      Name?: string;
      Type?: number;
      Dimension?: AisDimension;
    };
    ShipStaticData?: {
      UserID: number;
      Name?: string;
      Type?: number;
      Dimension?: AisDimension;
    };
    /** Class B static data (message type 24): name in Part A, type/size in Part B. */
    StaticDataReport?: {
      UserID: number;
      ReportA?: { Name?: string };
      ReportB?: { ShipType?: number; Dimension?: AisDimension };
    };
  };
}

export type PartialTarget = Partial<AisTarget> & { mmsi: number };

/** Overall length (m) from AIS dimensions: distance to bow (A) + to stern (B). */
function overallLength(dim?: AisDimension): number | undefined {
  const a = dim?.A;
  const b = dim?.B;
  return a != null && b != null && a + b > 0 ? a + b : undefined;
}

/** Overall beam (m) from AIS dimensions: distance to port (C) + to starboard (D). */
function overallBeam(dim?: AisDimension): number | undefined {
  const c = dim?.C;
  const d = dim?.D;
  return c != null && d != null && c + d > 0 ? c + d : undefined;
}

/**
 * The raw AIS ship-type code when it's a real value. 0 is the AIS "not available"
 * sentinel (same as an absent type), so we drop it rather than store a 0 that would
 * later read as a genuine "unknown vessel" code and, on a merge, clobber a good one.
 */
function validShipType(n?: number): number | undefined {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : undefined;
}

/** A finite number guard (rejects undefined, null, NaN, Infinity). */
function isFiniteNum(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * The vessel's position for a report: the AIS body's Longitude/Latitude when they
 * are real numbers, otherwise the MetaData fix AISStream attaches to the frame.
 * Returns null when neither gives a usable [lon, lat], so the caller can skip it.
 */
function reportPosition(
  pr: AisPositionCommon,
  metaPos?: [number, number] | null,
): [number, number] | null {
  if (isFiniteNum(pr.Longitude) && isFiniteNum(pr.Latitude)) return [pr.Longitude, pr.Latitude];
  if (metaPos && isFiniteNum(metaPos[0]) && isFiniteNum(metaPos[1])) return metaPos;
  return null;
}

/**
 * Build a partial target from any position report. Class A and Class B position
 * reports share these fields, so one helper covers all three. TrueHeading 511 is
 * the AIS "not available" sentinel, so we drop it rather than draw a heading north.
 * Cog/Sog are left off when AISStream doesn't send them (a moored or newly seen
 * boat), and the store merge treats a missing course/speed as unknown rather than
 * a reason to drop the vessel. `metaPos` is the frame's MetaData fix, used only
 * when the AIS body has no usable position of its own.
 */
function positionTarget(
  pr: AisPositionCommon,
  shipName?: string,
  metaPos?: [number, number] | null,
): PartialTarget {
  const hdg = pr.TrueHeading != null && pr.TrueHeading !== 511 ? pr.TrueHeading : undefined;
  const pos = reportPosition(pr, metaPos);
  return {
    mmsi: pr.UserID,
    ...(pos ? { position: pos } : {}),
    cogDeg: isFiniteNum(pr.Cog) ? pr.Cog : 0,
    sogKn: isFiniteNum(pr.Sog) ? pr.Sog : 0,
    headingDeg: hdg,
    name: shipName,
    lastMs: Date.now(),
  };
}

/**
 * Parse one AISStream JSON frame into a partial target, or null for frames we
 * don't use. Pure and unit-tested — the network client just feeds it each frame.
 * Returns `{ error }` shape via the caller; here an error frame yields null and
 * the caller reads `frame.error` separately.
 */
export function parseAisStreamFrame(frame: AisStreamFrame): PartialTarget | null {
  const shipName = frame.MetaData?.ShipName ? String(frame.MetaData.ShipName).trim() : undefined;
  // AISStream also attaches the last-known fix on MetaData (lowercase lon/lat). We
  // use it only when a position report's own body has no usable Longitude/Latitude.
  const metaPos: [number, number] | null =
    isFiniteNum(frame.MetaData?.longitude) && isFiniteNum(frame.MetaData?.latitude)
      ? [frame.MetaData.longitude, frame.MetaData.latitude]
      : null;

  // Class A position (message types 1/2/3).
  if (frame.MessageType === 'PositionReport' && frame.Message?.PositionReport) {
    return positionTarget(frame.Message.PositionReport, shipName, metaPos);
  }
  // Class B position (message type 18). This is what most leisure craft transmit,
  // so ignoring it is exactly why a busy anchorage like Tobermory showed zero
  // targets while every boat was plain to see on MarineTraffic.
  if (frame.MessageType === 'StandardClassBPositionReport' && frame.Message?.StandardClassBPositionReport) {
    return positionTarget(frame.Message.StandardClassBPositionReport, shipName, metaPos);
  }
  // Extended Class B position (message type 19): a position report that also
  // carries the vessel's name, type, and size, so pull those in too.
  if (frame.MessageType === 'ExtendedClassBPositionReport' && frame.Message?.ExtendedClassBPositionReport) {
    const pr = frame.Message.ExtendedClassBPositionReport;
    const t = positionTarget(pr, shipName, metaPos);
    const name = (pr.Name ? String(pr.Name).trim() : undefined) || shipName;
    const shipType = shipTypeLabel(pr.Type);
    const shipTypeCode = validShipType(pr.Type);
    const lengthM = overallLength(pr.Dimension);
    const beamM = overallBeam(pr.Dimension);
    // Only set the extra fields when present so we never clobber a good value
    // (from a static-data frame) with an undefined one on the store merge.
    return {
      ...t,
      ...(name ? { name } : {}),
      ...(shipType ? { shipType } : {}),
      ...(shipTypeCode != null ? { shipTypeCode } : {}),
      ...(lengthM != null ? { lengthM } : {}),
      ...(beamM != null ? { beamM } : {}),
    };
  }
  // Class A static data (message type 5): name, type, dimensions.
  if (frame.MessageType === 'ShipStaticData' && frame.Message?.ShipStaticData) {
    const sd = frame.Message.ShipStaticData;
    const name = (sd.Name ? String(sd.Name).trim() : undefined) || shipName;
    const shipType = shipTypeLabel(sd.Type);
    const shipTypeCode = validShipType(sd.Type);
    const lengthM = overallLength(sd.Dimension);
    const beamM = overallBeam(sd.Dimension);
    // Conditional so a static frame missing a field can't overwrite a good value
    // already merged from an earlier frame (the name-merge rule, applied to all).
    return {
      mmsi: sd.UserID,
      ...(name ? { name } : {}),
      ...(shipType ? { shipType } : {}),
      ...(shipTypeCode != null ? { shipTypeCode } : {}),
      ...(lengthM != null ? { lengthM } : {}),
      ...(beamM != null ? { beamM } : {}),
      lastMs: Date.now(),
    };
  }
  // Class B static data (message type 24): name in Part A, type/size in Part B.
  // The two parts can arrive separately, so only set the fields we actually got.
  if (frame.MessageType === 'StaticDataReport' && frame.Message?.StaticDataReport) {
    const sd = frame.Message.StaticDataReport;
    const name = (sd.ReportA?.Name ? String(sd.ReportA.Name).trim() : undefined) || shipName;
    const shipType = shipTypeLabel(sd.ReportB?.ShipType);
    const shipTypeCode = validShipType(sd.ReportB?.ShipType);
    const lengthM = overallLength(sd.ReportB?.Dimension);
    const beamM = overallBeam(sd.ReportB?.Dimension);
    return {
      mmsi: sd.UserID,
      ...(name ? { name } : {}),
      ...(shipType ? { shipType } : {}),
      ...(shipTypeCode != null ? { shipTypeCode } : {}),
      ...(lengthM != null ? { lengthM } : {}),
      ...(beamM != null ? { beamM } : {}),
      lastMs: Date.now(),
    };
  }
  return null;
}

/**
 * Merge one parsed partial onto the existing target for its MMSI, deciding whether
 * the result is worth storing. Pure and unit-tested; the runner just calls this and
 * upserts a non-null result.
 *
 * The ONLY thing a live vessel must have to be plotted is a valid position. Course
 * and speed are often absent (a moored boat, a Class B set that isn't sending them,
 * a static-data frame arriving first), so a missing Cog/Sog is treated as unknown
 * and normalised to 0, NOT as a reason to drop the target. Gating on finite cog/sog
 * — as an earlier version did — silently binned every such vessel, which is exactly
 * how a busy anchorage plots as empty. A frame with no usable position (e.g. pure
 * static data before any position report) returns null and is skipped.
 */
export function mergeAisTarget(cur: AisTarget | undefined, partial: PartialTarget): AisTarget | null {
  const merged = { ...cur, ...partial } as AisTarget;
  const pos = merged.position;
  const hasPosition =
    Array.isArray(pos) && pos.length === 2 && Number.isFinite(pos[0]) && Number.isFinite(pos[1]);
  if (!hasPosition) return null;
  if (!Number.isFinite(merged.cogDeg)) merged.cogDeg = 0;
  if (!Number.isFinite(merged.sogKn)) merged.sogKn = 0;
  return merged;
}

/**
 * How long the socket may sit OPEN with no frame at all before we treat the feed as
 * stalled and force a reconnect. AISStream in populated water sends frames every few
 * seconds, so a gap this long while we're still holding targets means the connection
 * has gone silent (a half-open TCP socket, the WebView backgrounded, coverage
 * dropped) rather than the water having emptied. Deliberately well under the target
 * lost threshold (AGE_LOST_MS = 6 min) so we tear down and repopulate BEFORE every
 * contact ages across "lost" at once — which is exactly the "they all seem to be lost
 * now" the skipper saw when the feed died quietly and nothing reconnected.
 */
export const AIS_STALL_MS = 120_000; // 2 min

/** How often the stall watchdog checks the socket. */
export const AIS_WATCHDOG_TICK_MS = 15_000;

/**
 * Is a live AIS feed stalled? True when the socket is meant to be up, we're still
 * holding targets, and no frame has arrived for `stallMs`. We gate on holding
 * targets on purpose: genuinely empty water can be silent for ages (AISStream is
 * shore-based, so a remote loch really is quiet), and we must not cry "feed lost"
 * there. It's only suspicious when we HAD ships and the frames stopped — then those
 * contacts are all ageing toward "lost" together and a reconnect is the right move.
 * Pure and clock-injected so the watchdog decision is easy to test.
 */
export function aisFeedStalled(opts: {
  lastFrameMs: number;
  nowMs: number;
  targetCount: number;
  stallMs?: number;
}): boolean {
  if (opts.targetCount <= 0) return false;
  const stall = opts.stallMs ?? AIS_STALL_MS;
  return opts.nowMs - opts.lastFrameMs >= stall;
}

export interface AisStreamHandle {
  updateBbox: (b: Bbox) => void;
  close: () => void;
}

/**
 * Decode one WebSocket frame's payload to text, whatever shape the transport hands
 * back. This is the heart of the online-AIS fix. AISStream sends its JSON frames as
 * BINARY, so a browser/WebView delivers each MessageEvent.data as a Blob (or, once
 * we set binaryType = 'arraybuffer', an ArrayBuffer), NOT a string. Calling
 * JSON.parse on a Blob turns it into the literal "[object Blob]" and throws, so on
 * the packaged app every frame was silently dropped and no ship ever reached the
 * store. Node's `ws` library hands back Buffers that JSON.parse quietly coerces,
 * which is why dev and the tests passed while the APK stayed empty. We cover all
 * shapes: a string is used as-is; an ArrayBuffer, or any typed-array / DataView
 * view over one, is decoded with TextDecoder (respecting the view's byte offset and
 * length); a Blob is read with its async .text(). Returns a Promise so the async
 * Blob path shares one call site with the synchronous ones. Both transports
 * (direct-native and the dev/relay proxy) run through the same client below, so
 * this one helper fixes both.
 */
export function decodeAisFrame(data: unknown): Promise<string> {
  if (typeof data === 'string') return Promise.resolve(data);
  // A typed-array / DataView / Node Buffer view. ArrayBuffer.isView reads an
  // internal slot, so it stays true even across JS realms (which matters under the
  // jsdom test runtime). Respect the view's own offset and length so a framed
  // payload isn't read together with surrounding bytes.
  if (ArrayBuffer.isView(data)) {
    const view = data as ArrayBufferView;
    return Promise.resolve(
      new TextDecoder().decode(new Uint8Array(view.buffer, view.byteOffset, view.byteLength)),
    );
  }
  // `data instanceof ArrayBuffer` is unreliable across realms (a Buffer built in
  // one realm fails the check in another), so match on the brand tag instead.
  const tag = Object.prototype.toString.call(data);
  if (tag === '[object ArrayBuffer]') {
    return Promise.resolve(new TextDecoder().decode(new Uint8Array(data as ArrayBuffer)));
  }
  if (tag === '[object Blob]' || (typeof Blob !== 'undefined' && data instanceof Blob)) {
    const blob = data as Blob;
    // A real browser/WebView Blob has async .text(); prefer it. Fall back to
    // .arrayBuffer(), then to FileReader, so a leaner Blob (as jsdom provides in
    // tests) still decodes rather than throwing.
    if (typeof blob.text === 'function') return blob.text();
    if (typeof blob.arrayBuffer === 'function') {
      return blob.arrayBuffer().then((b) => new TextDecoder().decode(new Uint8Array(b)));
    }
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('Blob read failed'));
      reader.readAsText(blob);
    });
  }
  // Anything else: coerce to string so a JSON.parse attempt still gets a fair go.
  return Promise.resolve(String(data));
}

export function connectAisStream(
  transport: AisTransport,
  bbox: Bbox,
  onTarget: (t: PartialTarget) => void,
  onState: (s: AisConnState, msg?: string) => void,
  /**
   * How many targets we're currently holding. The stall watchdog reads this so it
   * only reconnects a SILENT feed when we actually have contacts going stale (empty
   * water is legitimately quiet and must not trigger a false reconnect). Optional so
   * existing callers/tests are unaffected; absent means "don't second-guess silence".
   */
  getTargetCount?: () => number,
): AisStreamHandle {
  let ws: WebSocket | null = null;
  let closed = false;
  let lastBbox = bbox;
  let retry = 0;
  const url = transport.url ?? AISSTREAM_WS_URL;
  const key = transport.subscribeKey ?? '';
  let parseFailures = 0;
  // When we last heard ANY frame. The watchdog compares this against the clock to
  // spot a socket that's still "open" but has gone quiet. Seeded to now so a fresh
  // connection always gets a full stall window before it can be judged stalled.
  let lastFrameMs = Date.now();
  let watchdog: ReturnType<typeof setInterval> | null = null;

  /**
   * Decode + parse one raw frame and dispatch it. Kept off the WebSocket's
   * onmessage so the async decode (Blob.text) can be awaited in arrival order.
   * Decode and JSON errors are counted and logged rather than silently swallowed,
   * so a future upstream format change surfaces in the console instead of quietly
   * emptying the plot the way the Blob bug did. The normal binary path must, and
   * now does, succeed.
   */
  async function handleFrame(data: unknown): Promise<void> {
    let frame: AisStreamFrame;
    try {
      const text = await decodeAisFrame(data);
      frame = JSON.parse(text) as AisStreamFrame;
    } catch {
      parseFailures += 1;
      if (parseFailures <= 3 || parseFailures % 100 === 0) {
        console.warn(`[ais] could not decode/parse a frame (dropped ${parseFailures} so far)`);
      }
      return;
    }
    if (frame.error) {
      onState('error', frame.error);
      return;
    }
    const t = parseAisStreamFrame(frame);
    if (t) onTarget(t);
  }

  /**
   * Tear the current socket down and open a fresh one straight away, WITHOUT going
   * through the onclose backoff path (that's for a socket that dropped on its own).
   * We detach the old handlers first so its late close/error can't fire into the new
   * connection, and re-seed the frame clock so the fresh socket gets a clean window.
   */
  function reconnectNow() {
    if (closed) return;
    const old = ws;
    ws = null;
    if (old) {
      old.onopen = null;
      old.onmessage = null;
      old.onerror = null;
      old.onclose = null;
      try {
        old.close();
      } catch {
        /* ignore */
      }
    }
    lastFrameMs = Date.now();
    open();
  }

  function open() {
    if (closed) return;
    onState('connecting');
    // Fresh socket: give it a full stall window before the watchdog can judge it.
    lastFrameMs = Date.now();
    ws = new WebSocket(url);
    // AISStream delivers its JSON frames as BINARY. The default binaryType is
    // 'blob', which would force an async Blob.text() per frame; asking for
    // arraybuffer lets the common path decode synchronously. decodeAisFrame still
    // copes with a Blob for any WebView that ignores this hint.
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      retry = 0;
      lastFrameMs = Date.now();
      ws?.send(buildAisSubscribeMessage(key, lastBbox));
      onState('live');
    };
    // Decoding a Blob is async, so chain each frame onto the previous one. That
    // keeps frames in arrival order and guarantees an await can never drop or
    // reorder one, whatever shape the transport hands us.
    let chain: Promise<void> = Promise.resolve();
    ws.onmessage = (ev) => {
      // Stamp the moment ANY frame arrives (before decode), so the watchdog sees a
      // live feed as live even while a Blob decode is still in flight.
      lastFrameMs = Date.now();
      const data: unknown = ev.data;
      chain = chain.then(() => handleFrame(data));
    };
    ws.onerror = () => onState('error', 'WebSocket error');
    ws.onclose = () => {
      if (closed) return;
      onState('error', 'Disconnected — retrying');
      retry = Math.min(retry + 1, 6);
      setTimeout(() => open(), 1000 * retry);
    };
  }

  open();

  // Stall watchdog. onclose already recovers a socket that DROPS (with backoff), but
  // a WebSocket can also go silent while still reporting OPEN — no close event ever
  // fires, so nothing reconnects and every held target ages out to "lost" together.
  // On each tick, if the socket is open but we've heard nothing for AIS_STALL_MS
  // while still holding targets, surface it at feed level ('stalled') and reconnect
  // so the plot repopulates. Empty water stays quiet (aisFeedStalled gates on count).
  watchdog = setInterval(() => {
    if (closed || !ws || ws.readyState !== WebSocket.OPEN) return;
    const targetCount = getTargetCount ? getTargetCount() : 0;
    if (aisFeedStalled({ lastFrameMs, nowMs: Date.now(), targetCount })) {
      onState('stalled');
      reconnectNow();
    }
  }, AIS_WATCHDOG_TICK_MS);

  return {
    updateBbox: (b) => {
      lastBbox = b;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(buildAisSubscribeMessage(key, b));
    },
    close: () => {
      closed = true;
      if (watchdog != null) {
        clearInterval(watchdog);
        watchdog = null;
      }
      ws?.close();
    },
  };
}
